/**
 * BulkSearchResults — generic server-side bulk search results surface.
 *
 * Reads the queried ids from `?ids=ID1,ID2,...`, then for each page/filter
 * change dispatches the entity's search thunk (via the descriptor) and renders
 * the returned page in a Table. Pagination and the PropertyFilter are both
 * server-side: changing the page or a filter token re-queries the API rather
 * than slicing a client-side array. One component backs the payment, artifact,
 * and execution bulk pages — only the descriptor differs.
 */
import { useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import Button from '@cloudscape-design/components/button';
import CollectionPreferences from '@cloudscape-design/components/collection-preferences';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Pagination from '@cloudscape-design/components/pagination';
import PropertyFilter, { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import Tabs from '@cloudscape-design/components/tabs';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { regionLabel, withRegion } from 'src/constants';
import { BulkSearchDescriptor, queryToFilterMap } from './bulkSearchShared';

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
];
const EMPTY_QUERY: PropertyFilterProps.Query = { operation: 'and', tokens: [] };
/** The search API caps a single request at 100 identifiers. */
const MAX_IDS = 100;

export function BulkSearchResults<TRow>({ descriptor }: { descriptor: BulkSearchDescriptor<TRow> }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const ids = useMemo(() => {
    const raw = searchParams.get('ids') ?? '';
    return raw.split(',').map((s) => s.trim()).filter(Boolean).slice(0, MAX_IDS);
  }, [searchParams]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  /** Region tab chosen in a global search; null until the user picks one. */
  const [regionTab, setRegionTab] = useState<string | null>(null);
  const [query, setQuery] = useState<PropertyFilterProps.Query>(EMPTY_QUERY);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    descriptor.columns.map((c) => c.id!).filter(Boolean),
  );

  const filters = useMemo(() => queryToFilterMap(query), [query]);
  const {
    items, total, loading, error, regionResults, searchedRegions, regionErrors,
  } = useAppSelector(descriptor.select, shallowEqual);
  const { region, global } = useAppSelector((state) => state.env, shallowEqual);

  // Each region pages on its own, so no shared page number can address a global
  // result. Ids are capped at MAX_IDS, so one page that size per region returns
  // every match and the shared pager is hidden instead.
  const requestPage = global ? 1 : currentPage;
  const requestSize = global ? MAX_IDS : pageSize;

  // Fetch driver — re-queries on any page/size/filter change (all server-side).
  useEffect(() => {
    if (ids.length === 0) return;
    descriptor.run(dispatch, { ids, pageNumber: requestPage, pageSize: requestSize, filters });
  }, [descriptor, dispatch, ids, filters, requestPage, requestSize]);

  // Clear server state on unmount so a return visit starts clean.
  useEffect(() => () => { descriptor.clear(dispatch); }, [descriptor, dispatch]);

  const fannedOut = searchedRegions.length > 1;
  const pagesCount = Math.max(1, Math.ceil(total / pageSize));
  const activeColumns = descriptor.columns.filter((c) => c.id && visibleColumns.includes(c.id));
  const visibleContentOptions = descriptor.columns.map((c) => ({
    id: c.id!,
    label: typeof c.header === 'string' ? c.header : c.id!,
  }));

  const handleRefresh = () => {
    if (ids.length > 0) {
      descriptor.run(dispatch, { ids, pageNumber: requestPage, pageSize: requestSize, filters });
    }
  };

  const breadcrumbs = (
    <BreadcrumbGroup
      items={[
        { text: 'Search', href: withRegion('/', region) },
        { text: descriptor.title, href: withRegion(descriptor.path, region) },
      ]}
      onFollow={(e) => { e.preventDefault(); if (e.detail.href !== '#') navigate(e.detail.href); }}
    />
  );

  if (ids.length === 0) {
    return (
      <Box padding="l">
        <SpaceBetween size="l">
          {breadcrumbs}
          <Box textAlign="center" color="text-body-secondary">
            No {descriptor.entity} ids provided. Enter ids in bulk search mode on the search page.
          </Box>
        </SpaceBetween>
      </Box>
    );
  }

  const filterControl = (
    <PropertyFilter
      query={query}
      onChange={({ detail }) => { setQuery(detail); setCurrentPage(1); }}
      filteringProperties={descriptor.filteringProperties}
      countText={`${query.tokens.length} filter${query.tokens.length === 1 ? '' : 's'}`}
      filteringPlaceholder={`Filter ${descriptor.entity}s by status, state`}
      expandToViewport
    />
  );

  const preferencesControl = (
    <CollectionPreferences
      title="Preferences"
      confirmLabel="Confirm"
      cancelLabel="Cancel"
      preferences={{ pageSize, visibleContent: visibleColumns }}
      onConfirm={({ detail }) => {
        if (detail.pageSize && detail.pageSize !== pageSize) {
          setPageSize(detail.pageSize);
          setCurrentPage(1);
        }
        if (detail.visibleContent) setVisibleColumns([...detail.visibleContent]);
      }}
      pageSizePreference={fannedOut ? undefined : { title: 'Page size', options: PAGE_SIZE_OPTIONS }}
      visibleContentPreference={{
        title: 'Visible columns',
        options: [{ label: `${descriptor.title} columns`, options: visibleContentOptions }],
      }}
    />
  );

  const emptyBody = (
    <Box textAlign="center" padding={{ vertical: 'l' }}>
      <SpaceBetween size="s">
        <Box color="text-body-secondary">
          {error ?? `No ${descriptor.entity}s match the current filters.`}
        </Box>
        {error && <Button onClick={handleRefresh}>Retry</Button>}
      </SpaceBetween>
    </Box>
  );

  const spinner = (
    <Box textAlign="center" padding={{ vertical: 'xl' }}>
      <SpaceBetween size="s" alignItems="center">
        <Spinner size="large" />
        <Box color="text-body-secondary">Searching {descriptor.entity}s…</Box>
      </SpaceBetween>
    </Box>
  );

  // One region's rows, as the body of its tab. The tab label carries the
  // region, so the table itself is unheaded.
  const regionTabContent = (page: typeof regionResults[number]) => (
    <SpaceBetween size="s">
      {page.total > page.items.length && (
        <Box fontSize="body-s" color="text-body-secondary">
          Showing the first {page.items.length} of {page.total.toLocaleString()}. Search this
          region directly to page through the rest.
        </Box>
      )}
      <Table
        variant="embedded"
        resizableColumns
        sortingDisabled
        trackBy="id"
        items={page.items}
        columnDefinitions={activeColumns}
        empty={emptyBody}
      />
    </SpaceBetween>
  );

  if (fannedOut) {
    const withRows = regionResults.filter((page) => page.items.length > 0);
    // Fall back to the first region with rows, so a filter change that empties
    // the selected region cannot leave every tab unselected.
    const activeRegionTab = withRows.some((page) => page.region === regionTab)
      ? regionTab!
      : withRows[0]?.region;

    return (
      <Box padding="l">
        <SpaceBetween size="l">
          {breadcrumbs}

          <Container
            header={
              <Header
                variant="h2"
                counter={`(${items.length.toLocaleString()} across ${withRows.length} region${withRows.length === 1 ? '' : 's'})`}
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    {preferencesControl}
                    <Button iconName="refresh" onClick={handleRefresh} loading={loading}>
                      Refresh
                    </Button>
                  </SpaceBetween>
                }
              >
                {descriptor.title}
              </Header>
            }
          >
            {filterControl}
          </Container>

          {loading && items.length === 0 && spinner}

          {withRows.length > 0 && (
            <Tabs
              variant="container"
              activeTabId={activeRegionTab}
              onChange={({ detail }) => setRegionTab(detail.activeTabId)}
              tabs={withRows.map((page) => ({
                id: page.region,
                label: `${regionLabel(page.region)} (${page.items.length})`,
                content: regionTabContent(page),
              }))}
            />
          )}

          {!loading && withRows.length === 0 && regionErrors.length === 0 && (
            <Box textAlign="center" color="text-body-secondary">
              No {descriptor.entity}s matched in any of the {searchedRegions.length} regions.
            </Box>
          )}

          {regionErrors.length > 0 && (
            <Alert
              type="warning"
              header={`${regionErrors.length} of ${searchedRegions.length} regions did not respond`}
            >
              <SpaceBetween size="xxs">
                <Box variant="span">
                  These results are incomplete — a region that failed may hold more matches.
                </Box>
                {regionErrors.map((e) => (
                  <Box key={e.region} fontSize="body-s" color="text-status-error">
                    {regionLabel(e.region)}: {e.message}
                  </Box>
                ))}
              </SpaceBetween>
            </Alert>
          )}
        </SpaceBetween>
      </Box>
    );
  }

  return (
    <Box padding="l">
      <SpaceBetween size="l">
        {breadcrumbs}

        {loading && items.length === 0 && spinner}

        {(!loading || items.length > 0) && (
          <Table
            variant="container"
            resizableColumns
            sortingDisabled
            trackBy="id"
            loading={loading}
            loadingText={`Searching ${descriptor.entity}s…`}
            items={items}
            columnDefinitions={activeColumns}
            header={
              <Header
                variant="h2"
                counter={`(${items.length} of ${total.toLocaleString()})`}
                actions={
                  <Button iconName="refresh" onClick={handleRefresh} loading={loading}>
                    Refresh
                  </Button>
                }
              >
                {descriptor.title}
              </Header>
            }
            filter={filterControl}
            pagination={
              <Pagination
                currentPageIndex={currentPage}
                pagesCount={pagesCount}
                disabled={loading}
                onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
              />
            }
            preferences={preferencesControl}
            empty={emptyBody}
          />
        )}
      </SpaceBetween>
    </Box>
  );
}
